import { HttpInterceptorFn, HttpErrorResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { catchError, throwError, from, switchMap } from 'rxjs';
import { AuthService } from '../services/auth.service';

/**
 * Helper: Decode JWT payload without validation
 */
function decodeJwtPayload(token: string): any {
  try {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join(''),
    );
    return JSON.parse(jsonPayload);
  } catch (error) {
    console.error('Failed to decode JWT:', error);
    return null;
  }
}

/**
 * Helper: Check if token is expired based on exp claim
 */
function isTokenExpiredCheck(token: string): boolean {
  try {
    const payload = decodeJwtPayload(token);
    if (!payload || !payload.exp) {
      return false; // Can't determine, assume not expired
    }
    const now = Math.floor(Date.now() / 1000);
    return payload.exp < now;
  } catch (error) {
    return false;
  }
}

/**
 * Helper: Get token expiration date
 */
function getTokenExpiration(token: string): string {
  try {
    const payload = decodeJwtPayload(token);
    if (!payload || !payload.exp) {
      return 'Unknown';
    }
    return new Date(payload.exp * 1000).toISOString();
  } catch (error) {
    return 'Unknown';
  }
}

/**
 * HTTP Interceptor for authentication
 * - Adds JWT Bearer token to outgoing requests
 * - Handles authentication errors (401, 403)
 * - Distinguishes between token expired vs insufficient permissions
 * - Uses AuthService for token management
 * 
 * @example
 * Request flow:
 * 1. Gets access token from AuthService
 * 2. Adds Authorization header if token exists
 * 3. Handles 401 errors:
 *    - No token → logout immediately
 *    - Token expired (exp < now) → silent refresh via BFF → retry original request → logout only on failure
 *    - Token valid but 401 → "Permessi insufficienti" + redirect to /unauthorized (no logout)
 * 4. Handles 403 (Forbidden) → "Accesso negato" + redirect to /unauthorized
 */
export const authInterceptor: HttpInterceptorFn = (req, next) => {
  const router = inject(Router);

  // BFF auth endpoints use HttpOnly cookies — they neither need a Bearer token
  // nor should a 401 response from them trigger a force-logout.
  // IMPORTANT: this check must happen BEFORE inject(AuthService) to avoid a
  // circular dependency: AuthService constructor → http.post(silent-refresh)
  // → authInterceptor → inject(AuthService) → AuthService still constructing.
  const isBffAuthEndpoint =
    req.url.includes('/api/auth/silent-refresh') ||
    req.url.includes('/api/auth/token-exchange') ||
    req.url.includes('/api/auth/logout');

  if (isBffAuthEndpoint) {
    return next(req);
  }

  const authService = inject(AuthService);

  const currentReturnUrl = window.location.pathname + window.location.search + window.location.hash;

  const startReauthentication = (reason: string, message: string) => {
    void authService.startInteractiveReauthentication(currentReturnUrl, reason, message);
  };

  // Get token from AuthService (in-memory only)
  const token = authService.getAccessToken();

  // Clone request and add authorization header if token exists
  if (token) {
    req = req.clone({
      setHeaders: {
        Authorization: `Bearer ${token}`,
      },
    });
  }

  // Handle response and errors
  return next(req).pipe(
    catchError((error: HttpErrorResponse) => {
      if (error.status === 401) {
        // Check if token exists and is expired
        const token = authService.getAccessToken();
        const isTokenExpired = token ? isTokenExpiredCheck(token) : false;
        
        const errorDetails = {
          url: error.url,
          status: error.status,
          statusText: error.statusText,
          message: error.error?.message || error.message,
          timestamp: new Date().toISOString(),
          hasToken: !!token,
          tokenExpired: isTokenExpired,
        };
        
        if (!token) {
          // No token at all
          console.error('🚨 401 UNAUTHORIZED: No token found');
          console.error('Request URL:', req.url);
          startReauthentication('no_token', 'Sessione non disponibile. Effettua nuovamente il login.');
        } else if (isTokenExpired) {
          // Token expired — attempt a transparent silent refresh via BFF, then retry.
          // Only falls back to logout if the refresh_token cookie is also gone.
          console.warn('⏰ 401 UNAUTHORIZED: Token expired — attempting silent refresh');
          console.warn('Request URL:', req.url);
          console.warn('Token exp:', getTokenExpiration(token));

          return from(authService.trySilentRefresh()).pipe(
            switchMap(newToken => {
              if (newToken) {
                console.log('✅ Silent refresh succeeded — retrying original request');
                const retryReq = req.clone({
                  setHeaders: { Authorization: `Bearer ${newToken}` },
                });
                return next(retryReq);
              } else {
                console.error('❌ Silent refresh failed — session expired, requesting new login');
                startReauthentication('token_expired', 'Sessione scaduta. Effettua nuovamente il login.');
                return throwError(() => error);
              }
            }),
          );
        } else {
          // Token valid but insufficient permissions
          console.error('🔒 [INTERCEPTOR→/unauthorized] 401 UNAUTHORIZED: Valid token but insufficient permissions');
          console.error('  ➡ Request URL that caused 401:', req.url);
          console.error('  ➡ Response body:', error.error);
          console.error('  ➡ User roles in memory:', authService.getUserRoles());
          console.error('  ➡ Current browser URL at time of 401:', window.location.href);
          console.trace('  ➡ Stack trace (who triggered this HTTP call):');
          
          sessionStorage.setItem('auth_error_reason', 'insufficient_permissions');
          sessionStorage.setItem('auth_error_message', 'Non hai i permessi necessari per accedere a questa risorsa.');
          sessionStorage.setItem('last_401_error', JSON.stringify(errorDetails));
          
          // Don't logout - just redirect to unauthorized
          router.navigate(['/unauthorized']);
        }
      } else if (error.status === 403) {
        const token = authService.getAccessToken();
        const isTokenExpired = token ? isTokenExpiredCheck(token) : false;

        if (!token || isTokenExpired) {
          console.warn('⚠️ 403 received with missing/expired token — attempting silent refresh before treating as forbidden');

          return from(authService.trySilentRefresh()).pipe(
            switchMap(newToken => {
              if (newToken) {
                console.log('✅ Silent refresh succeeded after 403 — retrying original request');
                const retryReq = req.clone({
                  setHeaders: { Authorization: `Bearer ${newToken}` },
                });
                return next(retryReq);
              }

              console.error('❌ Silent refresh failed after 403 — requesting new login');
              startReauthentication('token_expired', 'Sessione scaduta. Effettua nuovamente il login.');
              return throwError(() => error);
            }),
          );
        }

        // Forbidden - user doesn't have permission
        console.warn('⚠️ [INTERCEPTOR→/unauthorized] 403 Forbidden - Access denied');
        console.error('  ➡ Request URL that caused 403:', req.url);
        console.error('  ➡ Response body:', error.error);
        console.error('  ➡ User roles in memory:', authService.getUserRoles());
        console.error('  ➡ Current browser URL at time of 403:', window.location.href);
        console.trace('  ➡ Stack trace (who triggered this HTTP call):');
        sessionStorage.setItem('auth_error_reason', 'forbidden');
        sessionStorage.setItem('auth_error_message', 'Accesso negato. Non disponi dei permessi necessari.');
        router.navigate(['/unauthorized']);
      }

      return throwError(() => error);
    }),
  );
};
